// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { RemoteMarkdown } from '@/components/remote/RemoteMarkdown';
import { MorphIcon } from '@/components/ui/morph-icon';
import { cn } from '@/lib/utils';
import type { ReasoningSummaryItem } from '@web/lib/remoteWorkItemModel';
import { Brain, Loader2 } from 'lucide';
import { formatDuration } from './presentation';

export interface ReasoningSummaryBlockProps {
  item: ReasoningSummaryItem;
  className?: string;
}

export function ReasoningSummaryBlock({
  item,
  className,
}: ReasoningSummaryBlockProps) {
  const summaryText = item.summaryText?.trim();
  const parts = (item.parts ?? [])
    .map((part) => {
      if (typeof part === 'string') return part;
      if (
        part &&
        typeof part === 'object' &&
        'text' in part &&
        typeof part.text === 'string'
      ) {
        return part.text;
      }
      return '';
    })
    .filter((part) => part.trim().length > 0);
  const empty = !summaryText && parts.length === 0;
  const active = item.status === 'in_progress';
  const durationLabel = formatDuration(item.durationMs);

  return (
    <section
      className={cn('min-w-0 py-2', className)}
      aria-label="Reasoning summary"
      aria-busy={active}
    >
      <header className="mb-2 flex min-w-0 items-center gap-2">
        {active ? (
          <MorphIcon
            icon={Loader2}
            aria-hidden
            className="h-4 w-4 shrink-0 animate-spin text-ds-icon-neutral-muted-default"
          />
        ) : (
          <MorphIcon
            icon={Brain}
            aria-hidden
            className="h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
          />
        )}
        <h3 className="m-0 min-w-0 flex-1 text-body-sm font-medium text-ds-text-neutral-default-default">
          Reasoning
        </h3>
        {durationLabel !== '—' && (
          <span className="shrink-0 text-label-xs tabular-nums text-ds-text-neutral-muted-default">
            {durationLabel}
          </span>
        )}
      </header>

      {summaryText ? (
        <RemoteMarkdown content={summaryText} />
      ) : parts.length > 0 ? (
        <div className="flex min-w-0 flex-col gap-2">
          {parts.map((part, index) => (
            <RemoteMarkdown key={`${item.id}:part:${index}`} content={part} />
          ))}
        </div>
      ) : (
        <p className="m-0 text-body-sm text-ds-text-neutral-muted-default">
          {active ? 'Thinking…' : 'No reasoning summary was recorded.'}
        </p>
      )}

      {empty && item.status === 'failed' && (
        <p className="sr-only">Reasoning ended with an error.</p>
      )}
    </section>
  );
}
